import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { supabase, getFlightBookings, getUserById } from './supabaseClient';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [chartPeriod, setChartPeriod] = useState('7');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState([
    { 
      label: 'Total Bookings', 
      value: '24,583', 
      change: '+12.5%', 
      trend: 'up',
      icon: '🎫' 
    },
    { 
      label: 'Total Revenue', 
      value: '₦892.4M', 
      change: '+18.2%', 
      trend: 'up',
      icon: '💰' 
    },
    { 
      label: 'Active Users', 
      value: '15,234', 
      change: '+8.3%', 
      trend: 'up',
      icon: '👥' 
    },
    { 
      label: 'Pending Refunds', 
      value: '0', 
      change: '0%', 
      trend: 'neutral',
      icon: '↩️' 
    },
  ]);
  const [bookingsData, setBookingsData] = useState([]);
  const [revenueByRoute, setRevenueByRoute] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [pendingActions, setPendingActions] = useState([
    { type: 'refund', label: 'Refund requests awaiting approval', count: 0 },
    { type: 'payment', label: 'Failed payments', count: 0 },
  ]);
  const [paymentStatusData, setPaymentStatusData] = useState([]);
  const [bookingStatusData, setBookingStatusData] = useState([]);

  // Fetch real data from Supabase
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Calculate date ranges
        const now = new Date();
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        // 1. Fetch total bookings count
        const { count: totalBookingsCount } = await supabase
          .from('flight_bookings')
          .select('*', { count: 'exact', head: true });

        // 2. Fetch total revenue from completed payments
        const { data: completedPayments } = await supabase
          .from('booking_payments')
          .select('amount')
          .eq('payment_status', 'completed');

        const totalRevenue = completedPayments?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;

        // 3. Fetch active users count
        const { count: activeUsersCount } = await supabase
          .from('users')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'active');

        // 4. Fetch pending refunds
        const { count: refundsCount } = await supabase
          .from('booking_payments')
          .select('*', { count: 'exact', head: true })
          .eq('payment_status', 'refunded');

        // 5. Fetch failed payments
        const { count: failedPaymentsCount } = await supabase
          .from('booking_payments')
          .select('*', { count: 'exact', head: true })
          .eq('payment_status', 'failed');

        // Update metrics
        setMetrics([
          { 
            label: 'Total Bookings', 
            value: totalBookingsCount?.toLocaleString() || '0', 
            change: '+0%', 
            trend: 'neutral',
            icon: '🎫' 
          },
          { 
            label: 'Total Revenue', 
            value: totalRevenue >= 1000000 
              ? `₦${(totalRevenue / 1000000).toFixed(1)}M` 
              : `₦${totalRevenue.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
            change: '+0%', 
            trend: 'neutral',
            icon: '💰' 
          },
          { 
            label: 'Active Users', 
            value: activeUsersCount?.toLocaleString() || '0', 
            change: '+0%', 
            trend: 'neutral',
            icon: '👥' 
          },
          { 
            label: 'Pending Refunds', 
            value: refundsCount?.toString() || '0', 
            change: '0%', 
            trend: 'neutral',
            icon: '↩️' 
          },
        ]);

        // 6. Fetch bookings for chart (last 7 or 30 days)
        const daysToFetch = chartPeriod === '7' ? 7 : 30;
        const startDate = new Date(now);
        startDate.setDate(startDate.getDate() - daysToFetch);

        const { data: bookingsForChart } = await supabase
          .from('flight_bookings')
          .select('booking_date')
          .gte('booking_date', startDate.toISOString())
          .order('booking_date', { ascending: true });

        // Group bookings by date
        const bookingsByDate = {};
        bookingsForChart?.forEach(booking => {
          const date = new Date(booking.booking_date).toLocaleDateString('en-CA');
          bookingsByDate[date] = (bookingsByDate[date] || 0) + 1;
        });

        // Create array for chart
        const chartData = [];
        for (let i = daysToFetch - 1; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(date.getDate() - i);
          const dateStr = date.toLocaleDateString('en-CA');
          chartData.push(bookingsByDate[dateStr] || 0);
        }
        setBookingsData(chartData);

        // 7. Fetch revenue by route
        const { data: bookingsWithRoutes } = await supabase
          .from('flight_bookings')
          .select('outbound_departure_city, outbound_arrival_city, outbound_departure_airport, outbound_arrival_airport, total_amount, payment_status')
          .eq('payment_status', 'paid')
          .limit(1000);

        const routeRevenue = {};
        bookingsWithRoutes?.forEach(booking => {
          const route = booking.outbound_departure_city && booking.outbound_arrival_city
            ? `${booking.outbound_departure_city} → ${booking.outbound_arrival_city}`
            : booking.outbound_departure_airport && booking.outbound_arrival_airport
            ? `${booking.outbound_departure_airport} → ${booking.outbound_arrival_airport}`
            : 'Unknown Route';
          const amount = parseFloat(booking.total_amount || 0);
          if (amount > 0) {
            routeRevenue[route] = (routeRevenue[route] || 0) + amount;
          }
        });

        const sortedRoutes = Object.entries(routeRevenue)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6);
        
        const totalRouteRevenue = sortedRoutes.reduce((sum, [, amount]) => sum + amount, 0);
        const routeData = sortedRoutes.map(([route, amount]) => ({
          route,
          amount,
          percentage: totalRouteRevenue > 0 ? Math.round((amount / totalRouteRevenue) * 100) : 0
        }));
        setRevenueByRoute(routeData);

        // 8. Fetch recent bookings
        const { data: recentBookingsData } = await supabase
          .from('flight_bookings')
          .select('*')
          .order('booking_date', { ascending: false })
          .limit(6);

        const recentBookingsWithUsers = await Promise.all(
          (recentBookingsData || []).map(async (booking) => {
            let userName = 'Unknown User';
            if (booking.user_id) {
              try {
                const user = await getUserById(booking.user_id);
                if (user) {
                  userName = user.name || user.email || 'Unknown User';
                }
              } catch (err) {
                console.warn('Could not fetch user:', err);
              }
            }

            const route = booking.outbound_departure_city && booking.outbound_arrival_city
              ? `${booking.outbound_departure_city} → ${booking.outbound_arrival_city}`
              : booking.outbound_departure_airport && booking.outbound_arrival_airport
              ? `${booking.outbound_departure_airport} → ${booking.outbound_arrival_airport}`
              : 'N/A';

            const amount = booking.total_amount
              ? `₦${parseFloat(booking.total_amount).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : '₦0.00';

            const bookingDate = booking.booking_date
              ? new Date(booking.booking_date).toLocaleDateString('en-NG', { year: 'numeric', month: '2-digit', day: '2-digit' })
              : 'N/A';

            const status = booking.booking_status 
              ? booking.booking_status.charAt(0).toUpperCase() + booking.booking_status.slice(1)
              : 'Pending';

            return {
              id: booking.booking_reference || booking.id.substring(0, 8).toUpperCase(),
              bookingId: booking.id,
              user: userName,
              route: route,
              date: bookingDate,
              amount: amount,
              status: status
            };
          })
        );
        setRecentBookings(recentBookingsWithUsers);

        // 9. Update pending actions
        setPendingActions([
          { type: 'refund', label: 'Refund requests awaiting approval', count: refundsCount || 0 },
          { type: 'payment', label: 'Failed payments', count: failedPaymentsCount || 0 },
        ]);

        // 10. Fetch payment status data for pie chart
        const { data: allPayments } = await supabase
          .from('booking_payments')
          .select('payment_status');

        const paymentStatusCounts = {};
        allPayments?.forEach(payment => {
          const status = payment.payment_status || 'unknown';
          paymentStatusCounts[status] = (paymentStatusCounts[status] || 0) + 1;
        });

        const paymentStatusChartData = Object.entries(paymentStatusCounts).map(([name, value]) => ({
          name: name.charAt(0).toUpperCase() + name.slice(1),
          value
        }));

        setPaymentStatusData(paymentStatusChartData);

        // 11. Fetch booking status data for pie chart
        const { data: allBookings } = await supabase
          .from('flight_bookings')
          .select('booking_status');

        const bookingStatusCounts = {};
        allBookings?.forEach(booking => {
          const status = booking.booking_status || 'unknown';
          bookingStatusCounts[status] = (bookingStatusCounts[status] || 0) + 1;
        });

        const bookingStatusChartData = Object.entries(bookingStatusCounts).map(([name, value]) => ({
          name: name.charAt(0).toUpperCase() + name.slice(1),
          value
        }));

        setBookingStatusData(bookingStatusChartData);

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [chartPeriod]);

  const maxBookings = bookingsData.length > 0 ? Math.max(...bookingsData) : 1;
  const maxRevenue = revenueByRoute.length > 0 ? Math.max(...revenueByRoute.map(r => r.amount)) : 1;

  // Colors for pie charts
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
  
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={12} fontWeight="bold">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const handleViewBooking = (bookingId) => {
    navigate(`/admin/bookings`);
  };

  const handlePendingAction = (type) => {
    if (type === 'refund' || type === 'payment') {
      navigate('/admin/payments');
    }
  };

  const handleQuickAction = (action) => {
    if (action === 'view-bookings') {
      navigate('/admin/bookings');
    } else if (action === 'view-payments') {
      navigate('/admin/payments');
    } else if (action === 'manage-users') {
      navigate('/admin/users');
    }
  };

  return (
    <AdminLayout pageTitle="Dashboard">
      <div className="admin-dashboard">
        {/* Page Title */}
        <div className="admin-dashboard-header">
          <h1 className="admin-dashboard-title">Dashboard</h1>
          <p className="admin-dashboard-subtitle">Overview of platform activity</p>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px',
            color: '#666'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
            <div>Loading dashboard data...</div>
          </div>
        )}

        {/* Top Metrics Section */}
        {!loading && (
          <>
          <div className="admin-metrics-section">
          <div className="admin-metrics-grid">
            {metrics.map((metric, index) => (
              <div key={index} className="admin-metric-card">
                <div className="admin-metric-icon">{metric.icon}</div>
                <div className="admin-metric-content">
                  <div className="admin-metric-label">{metric.label}</div>
                  <div className="admin-metric-value">{metric.value}</div>
                  <div className={`admin-metric-trend ${metric.trend}`}>
                    {metric.trend === 'up' ? '↑' : '↓'} {metric.change}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Charts Section */}
        <div className="admin-charts-section">
          <div className="admin-charts-grid">
            {/* Bookings Over Time Chart */}
            <div className="admin-chart-card">
              <div className="admin-chart-header">
                <h3 className="admin-chart-title">Bookings Over Time</h3>
                <div className="admin-chart-controls">
                  <button
                    className={`admin-chart-toggle ${chartPeriod === '7' ? 'active' : ''}`}
                    onClick={() => setChartPeriod('7')}
                  >
                    7 Days
                  </button>
                  <button
                    className={`admin-chart-toggle ${chartPeriod === '30' ? 'active' : ''}`}
                    onClick={() => setChartPeriod('30')}
                  >
                    30 Days
                  </button>
                </div>
              </div>
              <div className="admin-chart-content">
                <div className="admin-line-chart">
                  {bookingsData.map((value, index) => {
                    const height = (value / maxBookings) * 100;
                    return (
                      <div key={index} className="admin-chart-bar">
                        <div 
                          className="admin-chart-bar-fill"
                          style={{ height: `${height}%` }}
                          title={`${value} bookings`}
                        />
                        <div className="admin-chart-bar-label">
                          {chartPeriod === '7' ? `Day ${index + 1}` : index + 1}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Revenue by Route Chart */}
            <div className="admin-chart-card">
              <div className="admin-chart-header">
                <h3 className="admin-chart-title">Revenue by Route</h3>
              </div>
              <div className="admin-chart-content">
                <div className="admin-revenue-chart">
                  {revenueByRoute.map((item, index) => (
                    <div key={index} className="admin-revenue-item">
                      <div className="admin-revenue-header">
                        <span className="admin-revenue-route">{item.route}</span>
                        <span className="admin-revenue-amount">₦{(item.amount / 1000000).toFixed(1)}M</span>
                      </div>
                      <div className="admin-revenue-bar">
                        <div 
                          className="admin-revenue-bar-fill"
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                      <div className="admin-revenue-percentage">{item.percentage}%</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Payment Status Pie Chart */}
            <div className="admin-chart-card">
              <div className="admin-chart-header">
                <h3 className="admin-chart-title">Payment Status Distribution</h3>
              </div>
              <div className="admin-chart-content">
                {paymentStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={paymentStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {paymentStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    No payment data available
                  </div>
                )}
              </div>
            </div>

            {/* Booking Status Pie Chart */}
            <div className="admin-chart-card">
              <div className="admin-chart-header">
                <h3 className="admin-chart-title">Booking Status Distribution</h3>
              </div>
              <div className="admin-chart-content">
                {bookingStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={bookingStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {bookingStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    No booking data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Bookings Table */}
        <div className="admin-table-section">
          <div className="admin-table-card">
            <div className="admin-table-header">
              <h3 className="admin-table-title">Recent Bookings</h3>
            </div>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Booking ID</th>
                    <th>User</th>
                    <th>Route</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBookings.map((booking) => (
                    <tr key={booking.id} className="admin-table-row">
                      <td className="admin-table-cell admin-table-id">{booking.id}</td>
                      <td className="admin-table-cell">{booking.user}</td>
                      <td className="admin-table-cell">{booking.route}</td>
                      <td className="admin-table-cell">{booking.date}</td>
                      <td className="admin-table-cell admin-table-amount">{booking.amount}</td>
                      <td className="admin-table-cell">
                        <span className={`admin-status-badge admin-status-${booking.status.toLowerCase()}`}>
                          {booking.status}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <button
                          className="admin-table-action"
                          onClick={() => handleViewBooking(booking.bookingId || booking.id)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Bottom Section: Pending Actions & Quick Actions */}
        <div className="admin-actions-section">
          <div className="admin-actions-grid">
            {/* Pending Actions Panel */}
            <div className="admin-pending-card">
              <div className="admin-card-header">
                <h3 className="admin-card-title">Pending Actions</h3>
              </div>
              <div className="admin-card-content">
                <div className="admin-pending-list">
                  {pendingActions.map((action, index) => (
                    <div
                      key={index}
                      className="admin-pending-item"
                      onClick={() => handlePendingAction(action.type)}
                    >
                      <div className="admin-pending-info">
                        <div className="admin-pending-label">{action.label}</div>
                        <div className="admin-pending-count">{action.count}</div>
                      </div>
                      <div className="admin-pending-arrow">→</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="admin-quick-actions-card">
              <div className="admin-card-header">
                <h3 className="admin-card-title">Quick Actions</h3>
              </div>
              <div className="admin-card-content">
                <div className="admin-quick-actions-grid">
                  <button
                    className="admin-quick-action-btn"
                    onClick={() => handleQuickAction('add-flight')}
                  >
                    Add Flight
                  </button>
                  <button
                    className="admin-quick-action-btn"
                    onClick={() => handleQuickAction('view-bookings')}
                  >
                    View All Bookings
                  </button>
                  <button
                    className="admin-quick-action-btn"
                    onClick={() => handleQuickAction('view-payments')}
                  >
                    View Payments
                  </button>
                  <button
                    className="admin-quick-action-btn"
                    onClick={() => handleQuickAction('manage-users')}
                  >
                    Manage Users
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
